import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useAsyncDebounce,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from "react-table";
import {
  Avatar,
  Checkbox,
  FormControl,
  FormLabel,
  Flex,
  Icon,
  IconButton,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  TiArrowSortedDown,
  TiArrowSortedUp,
  TiArrowUnsorted
} from "react-icons/ti";
import {
  MdReplay,
  MdCheck
} from "react-icons/md";
import { IoMdClose } from "react-icons/io";
import IconBox from "components/Icons/IconBox";
import { SearchBar } from "components/Navbars/SearchBar/SearchBar";
import { TableBottom } from "components/Tables/TableBottom";
import { hasPermission } from "utils/permission";
import {
  PAGE_SIZE_OPTIONS,
  ASC,
  DESC
} from "constants/pagination";
import { AuthContext } from "contexts/AuthContext";
import { RowCardMobile } from "../Card/RowCardMobile";
import OrderBySelect from "./OrderBySelect";

function SearchTable2(props) {

  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const { columnsData, tableData, pageableData, onChange, maxVisiblePages, pageDivisor, setSearchTerm, searchTerm, truncatedColumns, noOfLines, disableSearch, onRowClick, includeSentBatches } = props;
  const pageable = useMemo(() => pageableData, [pageableData]);
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData ?? [], [tableData]);
  const hiddenColumns = useMemo(() => columns.filter(col => col.isHidden).map(col => col.id ?? col.accessor), [columnsData]);
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false });

  // Documentation requires custom sortBy to be memoized
  const sortBy = useMemo(() => (pageable?.orderBy ? [
    { id: pageable.orderBy, desc: pageable.orderDirection === DESC },
  ] : []), [pageable]);

  // Fill instance props as required by documentation or empty object for default behavior
  const overrideTableProps = useMemo(() => (pageable ? {
    manualPagination: true,
    manualSortBy: true,
    autoResetSortBy: false,
    disableMultiSort: true,
    pageCount: pageable.pageCount,
    initialState: { pageIndex: pageable.pageIndex, pageSize: pageable.pageSize, sortBy, hiddenColumns },
  } : {
    initialState: { hiddenColumns },
  }), [pageable]);

  const tableInstance = useTable({ columns, data, ...overrideTableProps }, useGlobalFilter, useSortBy, usePagination);
  const { getTableProps, getTableBodyProps, setGlobalFilter, prepareRow } = tableInstance;

  const handleParamsChange = (args) => {
    if (!onChange) return;
    const sortBy = tableInstance.state.sortBy?.[0];

    onChange({
      page:
        args.pageSize &&
        args.pageSize !== tableInstance.state.pageSize
          ? 1
          : tableInstance.state.pageIndex + 1,
      pageSize: args.pageSize ?? tableInstance.state.pageSize,
      orderBy: sortBy?.id,
      orderDirection: sortBy && (sortBy.desc ? DESC : ASC),
      term: searchTerm ?? pageable?.term,
      ...args,
    });
  }
  
  const handlePageChange = (page) => {
    if (!pageable) return;
    handleParamsChange({ page: page + 1 });
  }

  const handleSearchChange = useAsyncDebounce((value) => {
    if (searchTerm) return setSearchTerm(value || undefined);
    if (pageable) return handleParamsChange({ term: value || undefined, page: 1 });
    setGlobalFilter(value || undefined);
  }, 300);

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    if (!pageable) return tableInstance.setPageSize(newSize);
    handleParamsChange({ pageSize: newSize });
  }

  const handleSortByChange = (e, column) => {
    if (column.disableSortBy) return;

    if (!pageable) {
      const { onClick } = { ...column.getSortByToggleProps() };
      return onClick?.(e);
    }

    // Sort loop: unsorted > asc > desc > asc > desc
    const direction = column.isSorted
      ? (column.isSortedDesc ? ASC : DESC)
      : ASC;

    handleParamsChange({
      page: 1,
      orderBy: column.id,
      orderDirection: direction,
    });
  };


  return (
    <>
      <Flex w="100%" direction="column" overflowX={{ sm: "scroll", lg: "hidden" }}>
        <Flex w="100%" px="22px" mb="36px" gap="12px" align="center" justify="space-between" direction={{ sm: "column", md: "row" }}>
          <Flex gap="12px" justify="center" align="center" minW={{ sm: "200px", md: "200px" }}>
            <Select
              size="sm"
              variant="main"
              cursor="pointer"
              color={useColorModeValue("gray.500","gray.400")}
              maxW="75px"
              borderRadius="12px"
              value={tableInstance.state.pageSize}
              onChange={handlePageSizeChange}
            >
              {PAGE_SIZE_OPTIONS.map((size, key) => (
                <option key={key}>{size}</option>)
              )}
            </Select>

            <Text fontSize="xs" color={useColorModeValue("gray.500","gray.400")} fontWeight="normal">
              {t("system.labels.items-per-page")}
            </Text>
          </Flex>

          <Flex gap="12px" justify="center" align="center" minW={{ sm: "100px", md: "200px" }}>
            {!disableSearch && (
              <SearchBar
                border="1px solid "
                borderColor={useColorModeValue("gray.200", "gray.600")}
                placeholder={t("system.placeholders.search")}
                value={searchTerm ?? pageable?.term}
                autoFocus
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            )}

            {includeSentBatches && (
              <FormControl>
                <Flex alignItems="center">
                  <FormLabel htmlFor="include-sent" fontSize="sm" mb="0">
                    {t("system.shared-components.include-sent-batches")}
                  </FormLabel>
                  <Switch
                    id="include-sent"
                    isChecked={includeSentBatches.value}
                    onChange={(e) => includeSentBatches.onChange?.(e.target.checked)}
                  />
                </Flex>
              </FormControl>
            )}
          </Flex>
        </Flex>

        {isMobile ? (
            <>
              <OrderBySelect
                  tableInstance={tableInstance}
                  initialColumn={pageable?.orderBy}
                  initialDirection={pageable?.orderDirection}
                  onChange={({ column, direction }) => {
                    handleParamsChange({
                      page: 1,
                      orderBy: column,
                      orderDirection: direction,
                    });
                  }}
              />

              {tableInstance.page.map((row, index) => {
                prepareRow(row);
                return (
                    <RowCardMobile
                        key={index}
                        row={row}
                        index={index}
                        onRowClick={onRowClick}
                        user={user}
                        hasPermission={hasPermission}
                        truncatedColumns={truncatedColumns}
                        noOfLines={noOfLines}
                    />
                );
              })}
            </>
        ) : (
          <Table key="b511410e-554b-4fe7-8686-49a139952537" {...getTableProps()} color={useColorModeValue("gray.700","white")} mb="1rem">
            <Thead key="dd971f0c-3360-412b-9c58-5c081800fba1">
              {tableInstance.headerGroups.map((headerGroup, index) => (
                  <Tr key={index}>
                    {headerGroup.headers.map((column, index) => (
                        <Th key={index} pe="0px" onClick={e => handleSortByChange(e, column)}>
                          <Flex align="center" justify="space-between" color={useColorModeValue("gray.700","white")} fontSize={{ sm: "10px", lg: "12px" }}>
                            {column.render("Header")}

                            <IconBox
                                key={index}
                                float="right"
                                w={{ sm: "10px", md: "14px" }}
                                h={{ sm: "10px", md: "14px" }}
                                color={column.disableSortBy ? "gray.200" : column.isSorted ? "gray.500" : "gray.400"}
                            >
                              {column.isSorted ? (
                                  <Icon key={index} as={column.isSortedDesc ? TiArrowSortedDown : TiArrowSortedUp} />
                              ) : (
                                  <Icon key="db1403cc-ed56-4c7e-8902-6f726288ae29" as={TiArrowUnsorted} />
                              )}
                            </IconBox>
                          </Flex>
                        </Th>
                    ))}
                  </Tr>
              ))}
            </Thead>

            <Tbody {...getTableBodyProps()}>
              {tableInstance.page.map((row, index) => {
                prepareRow(row);
                return (
                    <Tr
                        {...row.getRowProps()}
                        key={index}
                        onDoubleClick={onRowClick ? () => onRowClick(row.original) : undefined}
                        bg={row?.original?.data_pendencia ? "#e4b9b9" : index % 2 === 0 ? useColorModeValue("gray.100", "navy.900") : useColorModeValue("white", "navy.800")}
                        _hover={{
                          bg: row?.original?.data_pendencia ? "#f2dede" : useColorModeValue("gray.200", "gray.600"),
                          color: row?.original?.data_pendencia ? "gray.700" : useColorModeValue("gray.700", "white"),
                          ...(onRowClick ? { cursor: "pointer" } : {}),
                        }}
                        transition="all 0.2s ease"
                    >
                      {row.cells.map((cell, index) => {
                        let data = "";
                        if (cell.column.Header === "STATUS") {
                          data = (
                              <Flex align="center">
                                <Flex
                                    justify="center"
                                    align="center"
                                    border="1px solid"
                                    borderColor={
                                      cell.value === "Paid"
                                          ? "green.400"
                                          : cell.value === "Refunded"
                                              ? "gray.400"
                                              : "red.400"
                                    }
                                    borderRadius="50%"
                                    width="24px"
                                    height="24px"
                                    me="6px"
                                >
                                  <Icon
                                      as={
                                        cell.value === "Paid"
                                            ? MdCheck
                                            : cell.value === "Refunded"
                                                ? MdReplay
                                                : IoMdClose
                                      }
                                      color={
                                        cell.value === "Paid"
                                            ? "green.400"
                                            : cell.value === "Refunded"
                                                ? "gray.400"
                                                : "red.400"
                                      }
                                      w="20px"
                                      h="20px"
                                  />
                                </Flex>
                                <Text>{cell.value}</Text>
                              </Flex>
                          );
                        } else if (cell.column.Header === "ID") {
                          data = (
                              <Flex align="center">
                                <Checkbox size="lg" colorScheme="blue" me="8px" />
                                <Text>{cell.value}</Text>
                              </Flex>
                          );
                        } else if (cell.column.Header === "DATE") {
                          data = <Text>{cell.value}</Text>;
                        } else if (cell.column.Header === "CUSTOMER") {
                          data = (
                              <Flex align="center">
                                <Avatar
                                    name={cell.value.split(" ").join()[0]}
                                    w="30px"
                                    h="30px"
                                    me="6px"
                                />
                                <Text>{cell.value}</Text>
                              </Flex>
                          );
                        } else if (cell.column.Header === "PRODUCT") {
                          data = <Text>{cell.value}</Text>;
                        } else if (cell.column.Header === "REVENUE") {
                          data = <Text>{cell.value}</Text>;
                        } else if (cell.column.actions?.length) {
                          const permittedActions = cell.column.actions.filter(action => {
                            const perms = Array.isArray(action.permissions)
                                ? action.permissions
                                : action.permissions
                                    ? [action.permissions]
                                    : [];

                            if (perms.length > 0 && !hasPermission({
                              userPermissions: user?.profilesPermissions,
                              requiredPermissions: perms
                            })) {
                              return false;
                            }

                            if (typeof action.visible === 'boolean') {
                              return action.visible;
                            }
                            if (typeof action.visible === 'function') {
                              return action.visible(cell.row.original);
                            }

                            return true;
                          });

                          if (permittedActions.length > 0) {
                            data = (
                                <>
                                  {permittedActions.map((action, idx) => (
                                      <Tooltip key={idx} label={action.label} hasArrow>
                                        <IconButton
                                            aria-label={action.label}
                                            icon={action.icon}
                                            onClick={() => action.onClick(cell.row)}
                                            colorScheme={action.colorScheme}
                                            border={`solid 0.1em ${useColorModeValue("#718096","#4A5568")}`}
                                            w="40px"
                                            h="35px"
                                            m="2px"
                                            p={action.p}
                                        />
                                      </Tooltip>
                                  ))}
                                </>
                            );
                          }
                        } else if (cell.column.custom) {
                          data = cell.column.custom(cell)
                        } else if (typeof truncatedColumns !== 'undefined') {
                          if (truncatedColumns.includes(cell.column.Header)) {
                            data = (
                                <Flex>
                                  <Tooltip label={cell.value} hasArrow>
                                    <Text noOfLines={noOfLines}>{cell.value}</Text>
                                  </Tooltip>
                                </Flex>
                            );
                          } else {
                            data = <Text>{cell.value}</Text>
                          }
                        }
                        return (
                            <Td
                                {...cell.getCellProps()}
                                key={index}
                                fontSize="sm"
                                minW={cell.column.minWidth ?? "100px"}
                                maxW={cell.column.maxWidth ?? "300px"}
                                p="5px 0 5px 20px"
                                width={`calc(100vw / ${tableInstance.visibleColumns.length})`}
                                whiteSpace="normal"
                                wordBreak="ellipsis"
                            >
                              {data || cell?.render('Cell')}
                            </Td>
                        );
                      })}
                    </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        <TableBottom
          tableInstance={tableInstance}
          maxVisiblePages={maxVisiblePages}
          pageDivisor={pageDivisor}
          totalElements={pageable?.totalElements}
          onPageChange={pageable && handlePageChange}
        />
      </Flex>
    </>
  );

}


export default SearchTable2;
